import {
  BaseDimension,
  BooleanDimension,
  ExclusionDimension,
  FinancialRateDimension,
  NumbersEnumDimension,
  StringsEnumDimension,
  StringsWithDataEnumDimension,
} from '@orus.eu/dimensions'
import {
  NL_RCPH_DEDUCTIBLES,
  NL_RCPH_DOUBLE_LIMITS,
  NL_RCPH_LIMITS,
  nlRcphCompanyLegalFormEntries,
  nlRcphProductVersions,
  type NlRcphCompanyLegalForm,
  type NlRcphDeductible,
  type NlRcphDoubleLimit,
  type NlRcphLimit,
} from '@orus.eu/product'

export { NL_RCPH_DEDUCTIBLES, NL_RCPH_DOUBLE_LIMITS, NL_RCPH_LIMITS }
export type { NlRcphDeductible, NlRcphDoubleLimit, NlRcphLimit }

export const nlRcphPreQuoteExclusionsDimension = new ExclusionDimension({
  name: 'nlRcphPreQuoteExclusions',
  displayKeys: { name: 'dimension_name_nl_rcph_pre_quote_exclusions' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: false },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const nlRcphExclusionsDimension = new ExclusionDimension({
  name: 'nlRcphExclusions',
  displayKeys: { name: 'dimension_name_nl_rcph_exclusions' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: false },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const nlRcphProductVersionDimension = new StringsEnumDimension({
  name: 'nlRcphProductVersion',
  displayKeys: { name: 'dimension_name_nl_rcph_product_version' },
  values: nlRcphProductVersions,
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const nlRcphCompanyExistingPolicyDimension = new BooleanDimension({
  name: 'nlRcphCompanyExistingPolicy',
  displayKeys: { name: 'dimension_name_nl_rcph_company_existing_policy' },
  considerForDifferenceComputationInUi: { client: true, partner: true, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export type { NlRcphCompanyLegalForm }
export const NL_RCPH_COMPANY_LEGAL_FORMS = nlRcphCompanyLegalFormEntries.map(([value]) => value)

export const nlRcphCompanyLegalFormDimension = new StringsWithDataEnumDimension({
  name: 'nlRcphCompanyLegalForm',
  displayKeys: { name: 'dimension_name_nl_rcph_company_legal_form' },
  entries: nlRcphCompanyLegalFormEntries,
  considerForDifferenceComputationInUi: { client: true, partner: true, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const nlRcphLimitDimension = new NumbersEnumDimension({
  name: 'nlRcphLimit',
  displayKeys: { name: 'dimension_name_nl_rcph_limit' },
  values: NL_RCPH_LIMITS,
  considerForDifferenceComputationInUi: { client: true, partner: true, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
  valuesAreAmount: true,
} as const)

export const nlRcphDoubleLimitDimension = new NumbersEnumDimension({
  name: 'nlRcphDoubleLimit',
  displayKeys: { name: 'dimension_name_nl_rcph_double_limit' },
  values: NL_RCPH_DOUBLE_LIMITS,
  considerForDifferenceComputationInUi: { client: true, partner: true, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
  valuesAreAmount: true,
} as const)

export const nlRcphDeductibleDimension = new NumbersEnumDimension({
  name: 'nlRcphDeductible',
  displayKeys: { name: 'dimension_name_nl_rcph_deductible' },
  values: NL_RCPH_DEDUCTIBLES,
  considerForDifferenceComputationInUi: { client: true, partner: true, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
  valuesAreAmount: true,
} as const)

export const nlRcphAnnualLimitDimension = new BaseDimension<'nlRcphAnnualLimit', number>({
  name: 'nlRcphAnnualLimit',
  displayKeys: { name: 'dimension_name_nl_rcph_annual_limit' },
  stringFormatter: (_value) => undefined,
  considerForDifferenceComputationInUi: { client: true, partner: true, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const nlRcphAllowedLimitsDimension = new BaseDimension<'nlRcphAllowedLimits', NlRcphLimit[]>({
  name: 'nlRcphAllowedLimits',
  displayKeys: { name: 'dimension_name_nl_rcph_allowed_limits' },
  stringFormatter: (_value) => undefined,
  considerForDifferenceComputationInUi: { client: true, partner: true, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const nlRcphAllowedDoubleLimitsDimension = new BaseDimension<'nlRcphAllowedDoubleLimits', NlRcphDoubleLimit[]>({
  name: 'nlRcphAllowedDoubleLimits',
  displayKeys: { name: 'dimension_name_nl_rcph_allowed_double_limits' },
  stringFormatter: (_value) => undefined,
  considerForDifferenceComputationInUi: { client: true, partner: true, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const nlRcphAllowedDeductiblesDimension = new BaseDimension<'nlRcphAllowedDeductibles', NlRcphDeductible[]>({
  name: 'nlRcphAllowedDeductibles',
  displayKeys: { name: 'dimension_name_nl_rcph_allowed_deductibles' },
  stringFormatter: (_value) => undefined,
  considerForDifferenceComputationInUi: { client: true, partner: true, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const nlRcphOverchargeRcRateDimension = new FinancialRateDimension({
  name: 'nlRcphOverchargeRcRate',
  displayKeys: { name: 'dimension_name_nl_rcph_overcharge_rc_rate' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: true,
  inputLocking: undefined,
  configuration: {
    min: 0,
    max: 10,
    step: 0.01,
  },
})

export const nlRcphInstallmentFeeRateDimension = new FinancialRateDimension({
  name: 'nlRcphInstallmentFeeRate',
  displayKeys: { name: 'dimension_name_nl_rcph_installment_fee_rate' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export function isNlRcphDeductible(value: number): value is NlRcphDeductible {
  const deductibles: readonly number[] = NL_RCPH_DEDUCTIBLES
  return deductibles.includes(value)
}
